import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { ReportHero } from '../components/MonthlyReport/ReportHero';
import { PatternCard } from '../components/MonthlyReport/PatternCard';
import { TrendSection } from '../components/MonthlyReport/TrendSection';
import { InsightCard } from '../components/MonthlyReport/InsightCard';
import { useAuth } from '../contexts/AuthContext';
import { useScans } from '../hooks/useScans';
import { Scan, Pattern } from '../types/scan';
import { ChevronDown, Lock, Crown, Zap } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
export function MonthlyReportPage() {
  const {
    user
  } = useAuth();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // Reliable pro status from DB
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const currentPlan = user?.user_metadata?.subscriptionPlan || 'free';
  const isPro = profile?.is_pro || false;
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    }
    fetchProfile();
    // Real-time subscription for profile updates
    const channel = supabase.channel('profile-monthly-report-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  // Calculate monthly stats
  const stats = useMemo(() => {
    if (!scans || scans.length === 0) return null;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    // Filter for current month scans
    const monthlyScans = scans.filter(s => {
      const d = new Date(s.created_at);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear && s.status === 'completed';
    });
    // Filter for previous month scans
    const prevMonthScans = scans.filter(s => {
      const d = new Date(s.created_at);
      const isPrevMonth = currentMonth === 0 ? d.getMonth() === 11 && d.getFullYear() === currentYear - 1 : d.getMonth() === currentMonth - 1 && d.getFullYear() === currentYear;
      return isPrevMonth && s.status === 'completed';
    });
    // Calculate scores
    const avgScore = monthlyScans.length > 0 ? monthlyScans.reduce((sum, s) => sum + (s.score || 0), 0) / monthlyScans.length : 0;
    const prevAvgScore = prevMonthScans.length > 0 ? prevMonthScans.reduce((sum, s) => sum + (s.score || 0), 0) / prevMonthScans.length : 0;
    // Aggregate patterns
    const patternCounts: Record<string, number> = {};
    monthlyScans.forEach(scan => {
      // Add type guard to ensure patterns is an array
      if (Array.isArray(scan.patterns)) {
        scan.patterns.forEach(p => {
          patternCounts[p.type] = (patternCounts[p.type] || 0) + 1;
        });
      }
    });
    const prevPatternCounts: Record<string, number> = {};
    prevMonthScans.forEach(scan => {
      // Add type guard to ensure patterns is an array
      if (Array.isArray(scan.patterns)) {
        scan.patterns.forEach(p => {
          prevPatternCounts[p.type] = (prevPatternCounts[p.type] || 0) + 1;
        });
      }
    });
    // Sort patterns by frequency
    const topPatterns = Object.entries(patternCounts).sort(([, a], [, b]) => b - a).slice(0, 3).map(([type, count]) => {
      const prevCount = prevPatternCounts[type] || 0;
      // Normalize by number of scans to get accurate trend
      const currentFreq = count / (monthlyScans.length || 1);
      const prevFreq = prevCount / (prevMonthScans.length || 1);
      let trend: 'up' | 'down' | 'stable' = 'stable';
      if (currentFreq > prevFreq * 1.1) trend = 'up';
      if (currentFreq < prevFreq * 0.9) trend = 'down';
      return {
        type,
        count,
        trend
      };
    });
    // Categorize trends for all patterns
    const improved = [];
    const stable = [];
    const worsened = [];
    const allTypes = new Set([...Object.keys(patternCounts), ...Object.keys(prevPatternCounts)]);
    allTypes.forEach(type => {
      const currentCount = patternCounts[type] || 0;
      const prevCount = prevPatternCounts[type] || 0;
      // Calculate percentage change
      const diff = currentCount - prevCount;
      const percentChange = prevCount > 0 ? Math.round(diff / prevCount * 100) : 100;
      if (diff < 0) improved.push({
        type,
        count: currentCount,
        change: percentChange
      });else if (diff > 0) worsened.push({
        type,
        count: currentCount,
        change: percentChange
      });else stable.push({
        type,
        count: currentCount,
        change: 0
      });
    });
    return {
      monthName: now.toLocaleString('default', {
        month: 'long'
      }),
      year: currentYear,
      avgScore,
      prevAvgScore,
      topPatterns,
      trends: {
        improved,
        stable,
        worsened
      },
      scanCount: monthlyScans.length
    };
  }, [scans]);
  if (loading || loadingProfile) {
    return <div className="min-h-screen bg-gray-950 flex items-center justify-center" data-id="element-9364">
        <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" data-id="element-9365" />
      </div>;
  }
  if (!stats || stats.scanCount === 0) {
    return <div className="min-h-screen bg-gray-950 text-white flex flex-col" data-id="element-9366">
        <WebsiteHeader data-id="element-9367" />
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-9368">
          <h1 className="text-4xl font-bold mb-4" data-id="element-9369">No Data Yet</h1>
          <p className="text-gray-400 mb-8 max-w-md" data-id="element-9370">
            Upload some chat logs to generate your first monthly communication
            report.
          </p>
          <a href="/upload" className="px-6 py-3 bg-purple-600 rounded-full font-bold hover:bg-purple-700 transition-colors" data-id="element-9371">
            Start a Scan
          </a>
        </div>
      </div>;
  }
  // Pro gate: free users see limited report with upgrade CTA
  if (!isPro) {
    return <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden selection:bg-purple-500/30" data-id="element-9372">
        <div className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-white/5" data-id="element-9373">
          <WebsiteHeader data-id="element-9374" />
        </div>

        <main className="pt-20" data-id="element-9375">
          {/* Show hero with blurred/limited data */}
          <ReportHero month={stats.monthName} year={stats.year} score={stats.avgScore} previousScore={stats.prevAvgScore} data-id="element-9376" />

          {/* Upgrade Gate Overlay */}
          <section className="max-w-2xl mx-auto px-4 py-20" data-id="element-9377">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="relative bg-[#0a0a0a] border-2 border-[#FFD700]/30 p-8 md:p-12 text-center overflow-hidden" data-id="element-9378">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FFD700]" data-id="element-9379" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FFD700]" data-id="element-9380" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FFD700]" data-id="element-9381" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FFD700]" data-id="element-9382" />

              {/* Scanning Line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent animate-[scan_4s_ease-in-out_infinite]" data-id="element-9383" />

              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFD700]/10 border border-[#FFD700] rounded-full mb-6" data-id="element-9384">
                <Lock className="w-8 h-8 text-[#FFD700]" data-id="element-9385" />
              </div>

              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-9386">
                <Crown className="w-5 h-5 text-[#FFD700]" data-id="element-9387" />
                <span className="text-[#FFD700] font-bold text-xs tracking-widest uppercase font-mono" data-id="element-9388">
                  Pro Feature
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9389">
                Monthly Clarity Report
              </h2>

              <p className="text-gray-400 mb-4 max-w-md mx-auto font-mono text-sm leading-relaxed" data-id="element-9390">
                Your report has{' '}
                <span className="text-white font-bold" data-id="element-9391">
                  {stats.scanCount} scans
                </span>{' '}
                analyzed with{' '}
                <span className="text-white font-bold" data-id="element-9392">
                  {stats.topPatterns.length} patterns
                </span>{' '}
                detected this month. Upgrade to Pro to unlock the full
                breakdown.
              </p>

              {/* Teaser stats */}
              <div className="grid grid-cols-3 gap-4 mb-8 max-w-sm mx-auto" data-id="element-9393">
                <div className="bg-gray-800/50 border border-gray-700 p-3" data-id="element-9394">
                  <div className="text-xl font-black text-white" data-id="element-9395">
                    {stats.scanCount}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-9396">
                    Scans
                  </div>
                </div>
                <div className="bg-gray-800/50 border border-gray-700 p-3" data-id="element-9397">
                  <div className="text-xl font-black text-white" data-id="element-9398">
                    {stats.topPatterns.length}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-9399">
                    Patterns
                  </div>
                </div>
                <div className="bg-gray-800/50 border border-gray-700 p-3" data-id="element-9400">
                  <div className="text-xl font-black text-white" data-id="element-9401">
                    {Math.round(stats.avgScore)}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-9402">
                    Avg Score
                  </div>
                </div>
              </div>

              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => setShowSubscriptionModal(true)} className="inline-flex items-center gap-2 bg-[#FFD700] text-black px-8 py-4 font-black uppercase tracking-wider hover:bg-[#FFD700]/90 transition-all shadow-[0_0_20px_rgba(255,215,0,0.4)]" data-id="element-9403">
                <Zap className="w-5 h-5 fill-black" data-id="element-9404" />
                Unlock Full Report
              </motion.button>

              <p className="text-xs text-gray-600 font-mono mt-4" data-id="element-9405">
                Pro members get unlimited monthly reports with full trend
                analysis
              </p>
            </motion.div>
          </section>
        </main>

        <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} currentPlan={currentPlan} data-id="element-9406" />
      </div>;
  }
  return <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden selection:bg-purple-500/30" data-id="element-9407">
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-white/5" data-id="element-9408">
        <WebsiteHeader data-id="element-9409" />
      </div>

      <main className="pt-20" data-id="element-9410">
        {/* Hero Section */}
        <ReportHero month={stats.monthName} year={stats.year} score={stats.avgScore} previousScore={stats.prevAvgScore} data-id="element-9411" />

        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1,
        y: [0, 10, 0]
      }} transition={{
        delay: 3,
        duration: 2,
        repeat: Infinity
      }} className="flex justify-center mb-20" data-id="element-9412">
          <ChevronDown className="text-gray-500" data-id="element-9413" />
        </motion.div>

        {/* Top Patterns Section */}
        <section className="max-w-6xl mx-auto px-4 mb-32" data-id="element-9414">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-9415">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-200 to-blue-200" data-id="element-9416">
              Recurring Themes
            </h2>
            <p className="text-gray-400" data-id="element-9417">
              The patterns that defined your conversations this month
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9418">
            {stats.topPatterns.map((pattern, idx) => <PatternCard key={pattern.type} type={pattern.type} count={pattern.count} trend={pattern.trend} rank={idx + 1} delay={idx * 0.2} data-id="element-9419" />)}
          </div>
        </section>

        {/* Trends Section */}
        <section className="bg-gray-900/50 py-24 mb-24 border-y border-white/5" data-id="element-9420">
          <div className="max-w-6xl mx-auto px-4 mb-16 text-center" data-id="element-9421">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-9422">
              Trend Analysis
            </h2>
            <p className="text-gray-400" data-id="element-9423">
              How your communication patterns are shifting
            </p>
          </div>

          <TrendSection improved={stats.trends.improved} stable={stats.trends.stable} worsened={stats.trends.worsened} data-id="element-9424" />
        </section>

        {/* Insight Section */}
        <section className="max-w-4xl mx-auto px-4 pb-32" data-id="element-9425">
          <InsightCard insight={stats.avgScore < 40 ? "You've maintained healthy boundaries this month. Continue practicing active listening to deepen your connections." : stats.avgScore < 70 ? 'We noticed some mixed signals. Try to pause before responding when you feel defensive.' : 'High tension detected. Consider setting clearer boundaries or taking breaks from heated conversations.'} actionLink="/blog" actionText="Explore Communication Tips" data-id="element-9426" />
        </section>
      </main>
    </div>;
}